import { PayPalButtons } from "@paypal/react-paypal-js";
import { FC, useState, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAsyncFn } from "src/hooks";
import { usePriceCalculation } from "src/hooks/usePriceCalculation";
import { requestPayment, updatePaymentStatus } from "src/services";
import { uploadFiles } from "src/services/payment";
import { useGlobalStore, useInstallationStore } from "src/store";
import { useFilesStore } from "src/store/files";
import { FormSchema } from "src/utils/formSchema";
import { showToast } from "src/utils/helpers";
import { PriceTableSchema, useGeneratePriceTable } from "src/utils/priceTable";
import { TERMS_AND_CONDITION_URL } from "src/utils/urls";
import PriceTable from "./PriceTable/PriceTable";

type PaypalCheckoutButtonProps = {
  form: UseFormReturn<FormSchema, unknown>;
};

const PaypalCheckoutButton: FC<PaypalCheckoutButtonProps> = ({ form }) => {
  const { global, setIsSubmitting, changeShowRequired, setSubmitType } =
    useGlobalStore();
  const { t } = useTranslation();
  const requestPaymentFn = useAsyncFn(requestPayment);
  const updatePaymentStatusFn = useAsyncFn(updatePaymentStatus);
  const uploadFilesFn = useAsyncFn(uploadFiles);
  const { installationDetails } = useInstallationStore();
  const [paymentRadio, setPaymentRadio] = useState("booked");
  const [paypalCorrelationID, setPaypalCorrelationID] = useState("");
  const { files } = useFilesStore();
  const { grandTotal } = usePriceCalculation(form);
  const priceTableInformation: PriceTableSchema[] = useGeneratePriceTable(form);

  const idRef = useRef<string | null>(null);

  const handlePaypalButtonInit = (initData: any) => {
    if (
      initData &&
      initData.correlationID &&
      initData.correlationID !== paypalCorrelationID
    ) {
      setPaypalCorrelationID(initData.correlationID);
    }
  };

  const redirectToSuccessPage = (paymentId: string) => {
    setTimeout(() => {
      if (window.top) {
        window.top.location.replace(
          `${process.env.REACT_APP_WORDPRESS}/success?orderNumber=${paymentId}`
        );
      }
    }, 1000);
  };

  const handleUpdatePaymentDetails = async (order: unknown) => {
    try {
      const currentId = idRef.current;

      const resp = await updatePaymentStatusFn.execute(currentId, order);

      showToast("paymentSuccess");
      redirectToSuccessPage(resp.data.paymentId);
    } catch (e) {
      showToast("paymentError", "error");
      setIsSubmitting(false);
    }
  };

  const handleApprove = async (order: unknown, type?: string) => {
    try {
      setIsSubmitting(true);
      const {
        salutation,
        firstName,
        lastName,
        companyName,
        email,
        telephoneNumber,
        contactStreetAndHouseNumber,
        contactCityAndZipCode,
        notes,

        zipCode,
        streetAndHouseNumber,
        timeFrom,
        timeTill,
        purpose,
        lengthOfBoard,
        dateRange,
      } = form.getValues();

      // Call backend function to fulfill order
      const resp = await requestPaymentFn.execute({
        order,
        userDetails: {
          firstName,
          salutation,
          lastName,
          companyName,
          email,
          telephoneNumber,
          contactStreetAndHouseNumber,
          contactCityAndZipCode,
          notes,
        },
        installationDetails: {
          ...installationDetails,
          zipCode,
          streetAndHouseNumber,
          timeFrom,
          timeTill,
          purpose,
          lengthOfBoard,
          dateRange,
        },
        type,
        priceTableInformation,
      });

      idRef.current = resp.data.id;

      if (files.length > 0) {
        await uploadFilesFn.execute(resp.data.id, files);
      }
      setIsSubmitting(false);

      if (type === "booked") {
        showToast("paymentSuccess");
        redirectToSuccessPage(resp.data.paymentId);
      }
      return [resp, null];
    } catch (e) {
      showToast("paymentError", "error");
      setIsSubmitting(false);
      return [null, e];
    }
  };

  const checkValidation = () => {
    if (grandTotal !== 0 && Object.keys(form.formState.errors).length === 0) {
      return true;
    } else {
      return false;
    }
  };

  const handlePaypalSubmit = () => {
    form.handleSubmit(() => {})();
    changeShowRequired();
  };

  const handleRadioChange = (value: string) => {
    if (value !== "pay_paypal") {
      setPaypalCorrelationID("");
    }
    setSubmitType(value);
    setPaymentRadio(value);
  };

  return (
    <>
      <div className="payment-checkout-container">
        <div style={{ marginTop: "20px" }}>
          <PriceTable
            priceTableInformation={priceTableInformation}
            form={form}
          />
        </div>
        <span>
          Mit Ihrer Bestellung bestätigen Sie, unsere <TermsAndConditionLink />{" "}
          und den Datenschutz gelesen und akzeptiert zu haben.
        </span>
      </div>
      <ul className="pay-now">
        <li>
          <label htmlFor="pay_paypal" className="radio-input-label">
            <input
              id="pay_paypal"
              type="radio"
              onChange={(e) => {
                handleRadioChange(e.currentTarget.value);
              }}
              checked={paymentRadio === "pay_paypal"}
              value="pay_paypal"
              name="Payment"
              className="radio-input"
            />
            <span className="paypal-span">PayPal</span>
            <img
              className="paypal-img"
              src="/assets/paypal-logo.png"
              alt="Paypal"
            />
          </label>
        </li>

        <li>
          <label htmlFor="booked" className="radio-input-label">
            <input
              id="booked"
              type="radio"
              onChange={(e) => handleRadioChange(e.currentTarget.value)}
              checked={paymentRadio === "booked"}
              value="booked"
              name="Payment"
              className="radio-input"
            />
            <span className="paypal-span">{t("main:booking")}</span>
          </label>
        </li>
      </ul>
      <div>
        {paymentRadio === "pay_paypal" && checkValidation() ? (
          <div className="text-align-center">
            <PayPalButtons
              style={{
                color: "blue",
                layout: "horizontal",
                height: 48,
                tagline: false,
                shape: "pill",
              }}
              onInit={handlePaypalButtonInit}
              disabled={!checkValidation()}
              onClick={handlePaypalSubmit}
              forceReRender={[installationDetails, paymentRadio, grandTotal]}
              createOrder={async (_, actions) => {
                const [, err] = await handleApprove(undefined);

                if (err) {
                  throw err;
                }

                return actions.order.create({
                  purchase_units: [
                    {
                      description: "product.description",
                      amount: {
                        value: grandTotal.toString(),
                      },
                    },
                  ],
                });
              }}
              onApprove={async (_, actions: any) => {
                const order = await actions.order.capture();
                await handleUpdatePaymentDetails({
                  ...order,
                  amount: grandTotal,
                });
              }}
              onCancel={() => {
                showToast("paymentCancelled", "error");
              }}
              className="paypal-button"
            />
          </div>
        ) : null}

        <button
          className="payment-btns"
          onClick={() => {
            form.handleSubmit(() => {
              if (checkValidation()) {
                if (paymentRadio === "pay_paypal") {
                  // Incase of Paypal, form is submitted onApprove of payment
                } else {
                  handleApprove({ amount: grandTotal }, paymentRadio);
                }
              }
            })();
            changeShowRequired();
          }}
          disabled={global.isSubmitting}
        >
          {global.isSubmitting ? (
            <img src="/assets/loading-gif.gif" height="25px" alt="loading" />
          ) : paypalCorrelationID || paymentRadio !== "pay_paypal" ? (
            "Jetzt bestellen"
          ) : (
            <img src="/assets/loading-gif.gif" height="25px" alt="loading" />
          )}
        </button>
      </div>
    </>
  );
};

export { PaypalCheckoutButton };

const TermsAndConditionLink = () => {
  const { t } = useTranslation();
  return (
    <a
      href={TERMS_AND_CONDITION_URL}
      rel="noreferrer"
      target="_blank"
      onClick={(e) => e.stopPropagation()}
      className="terms-and-condition"
    >
      {t("main:termsAndCondition:label")}
    </a>
  );
};
